import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import type { Tool } from './types';

type Input = { sources: Source[] };

type GeometryUnionInput = Parameters<typeof geometryEngine.union>[0];

export const computeUnion: Tool<Input> = {
  name: 'compute_union',
  description:
    'Compute the geometric union of two or more Sources. Returns a single geometry handle plus area in square meters (0 for non-polygon results). No map side-effects — pair with `render` to draw.',
  input_schema: {
    type: 'object',
    properties: {
      sources: {
        type: 'array',
        minItems: 2,
        items: SourceSchema,
      },
    },
    required: ['sources'],
    additionalProperties: false,
  },
  execute: async ({ sources }) => {
    if (!Array.isArray(sources) || sources.length < 2) {
      throw new Error('compute_union: need at least two sources');
    }

    const refs = await Promise.all(sources.map((s) => resolve(s)));
    const geometries: Geometry[] = [];
    for (let i = 0; i < refs.length; i++) {
      const g = refs[i].geometry ?? refs[i].extent;
      if (!g) throw new Error(`compute_union: source[${i}] has no geometry`);
      // For feature_set sources, prefer the per-feature list when available so the
      // union reflects every feature rather than the pre-merged extent.
      const fs = refs[i].feature_set;
      if (fs && fs.geometries.length > 0) {
        geometries.push(...fs.geometries);
      } else {
        geometries.push(g);
      }
    }

    const unioned = geometryEngine.union(geometries as unknown as GeometryUnionInput) as
      | Geometry
      | Geometry[]
      | null;
    const result = (Array.isArray(unioned) ? unioned[0] : unioned) as Geometry | null;
    if (!result) throw new Error('compute_union: union produced no geometry');

    const newRef: ResolvedRef = {
      source_kind: 'geometry',
      geometry: result,
      extent: (result as { extent?: __esri.Extent }).extent ?? undefined,
    };
    const handle = registerHandle(newRef);
    const area =
      result.type === 'polygon'
        ? geometryEngine.geodesicArea(
            result as Parameters<typeof geometryEngine.geodesicArea>[0],
            'square-meters',
          )
        : 0;

    return {
      handle,
      summary: summarizeRef(newRef),
      area_sq_m: area,
      input_count: geometries.length,
    };
  },
};
